/**
 * Touch motion — the card under the reader's attention takes its hover state.
 *
 * Hover is the site's main source of motion, and on a phone none of it fires.
 * On touch screens only (`hover: none`), this toggles `in-focus` on a card
 * while it crosses the middle of the screen, and elf-pages.css maps that class
 * onto the card's `:hover` state: needles slide, posters lift, photos brighten
 * as the reader scrolls.
 *
 * Runs for reduced-motion visitors too: it only toggles a class, and the
 * states it maps to are colour and lift, softened in that mode by CSS rather
 * than switched off here — stopping it left phones looking frozen.
 *
 * Deliberately decorative only. Nothing is ever hidden waiting for this to
 * fire: content that hover reveals on desktop simply rests visible on touch
 * screens, and its entrance is CSS, scroll-driven where the browser supports
 * it. A browser where this never runs loses motion, never content.
 */

import { useEffect, type RefObject } from 'react';

/** Cards whose `:hover` state is mirrored by `in-focus` in elf-pages.css. */
const FOCUS = '.tugrow, .poster, .prod, .post, .worktile';

export function useTouchMotion(rootRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (!window.matchMedia?.('(hover: none)').matches) return;

    // A band across the middle of the screen: the card inside it is the one
    // being "hovered".
    const focus = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) entry.target.classList.toggle('in-focus', entry.isIntersecting);
      },
      { rootMargin: '-38% 0px -38% 0px' },
    );

    const seen = new WeakSet<Element>();
    const scan = () => {
      root.querySelectorAll(FOCUS).forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        focus.observe(el);
      });
    };
    scan();

    // Cards arrive after mount — filter chips re-render the grids — so pick new
    // ones up as they appear, batched to one scan per frame.
    let queued = 0;
    const mutations = new MutationObserver(() => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        scan();
      });
    });
    mutations.observe(root, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(queued);
      mutations.disconnect();
      focus.disconnect();
    };
  }, [rootRef]);
}
